import { SignalBinding } from "signals";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiText from "slotworks/graphics/elements/flexi-text";
import Layers from "slotworks/graphics/layers/layers";
import * as TweenJS from "slotworks/utils/tween";

export default class PaginationComponent {

    protected contentLayer: Layers;
    protected uiLayer: Layers;

    protected uiSceneName: string;
    protected contentSceneNames: string[];

    protected mode: PaginationMode;

    protected page: number;
    protected totalPages: number;

    protected scrollOrigin: number;
    protected pageWidth: number;
    protected scrollTween: TweenJS.Tween;

    protected nextButton: FlexiButton;
    protected prevButton: FlexiButton;
    protected closeButton: FlexiButton;
    protected pageNumber: FlexiText;

    protected pageJumpListeners: Map<FlexiButton, () => any> = new Map<FlexiButton, () => any>();

    private contentListener: SignalBinding;

    constructor(contentLayer: Layers, uiLayer: Layers, uiSceneName: string, mode: PaginationMode = PaginationMode.PAGIFY) {
        this.contentSceneNames = [];
        this.contentLayer = contentLayer;
        this.uiLayer = uiLayer;
        this.uiSceneName = uiSceneName;
        this.mode = mode;

        for (let i = 1; i < Infinity; i++) {
            const page = "page_" + i.toString();
            if (!this.contentLayer.hasScene(page)) {
                break;
            }
            this.contentSceneNames.push(page);
        }

        this.totalPages = this.contentSceneNames.length;

        this.contentListener = this.contentLayer.onSceneEnter.add(this.onSceneEnter, this);

        this.hide();
    }

    public show = () => {
        this.onShow();
    }

    public hide = () => {
        this.onHide();
    }

    public enable() {
        this.updateControls();
    }

    public disable() {
        if (this.nextButton) {
            this.nextButton.setEnabled(false);
        }

        if (this.prevButton) {
            this.prevButton.setEnabled(false);
        }

        if (this.closeButton) {
            this.closeButton.setEnabled(false);
        }
    }

    public destroy() {
        this.onHide();
        if (this.nextButton) {
            this.nextButton.destroy();
            this.nextButton = null;
        }

        if (this.prevButton) {
            this.prevButton.destroy();
            this.prevButton = null;
        }

        if (this.closeButton) {
            this.closeButton.destroy();
            this.closeButton = null;
        }

        if (this.pageNumber) {
            this.pageNumber.destroy();
            this.pageNumber = null;
        }

        this.pageJumpListeners.clear();

        if (this.scrollTween) {
            this.scrollTween.stop();
            this.scrollTween = null;
        }

        this.contentListener.detach();
        this.contentListener = null;
    }

    public addControls(nextButton: FlexiButton, previousButton: FlexiButton, closeButton: FlexiButton, pageNumber?: FlexiText) {
        this.nextButton = nextButton;
        this.prevButton = previousButton;
        this.closeButton = closeButton;
        this.pageNumber = pageNumber;
    }

    protected onShow() {
        this.page = 0;

        this.contentLayer.show();

        if (this.mode === PaginationMode.SCROLL) {
            this.setupScrollableContent();
        }

        this.update();
    }

    protected onHide() {
        this.contentLayer.defaultScene().execute();
    }

    protected onSceneEnter(scene: string): void {
        const page: number = parseInt(scene.replace(/\D/g, "")) - 1;
        if (this.contentSceneNames[page]) {
            this.page = page;
            this.updateControls();
        }
    }

    protected reset() {
        if (this.nextButton) {
            this.nextButton.off(ButtonEvent.CLICK, this.nextPage);
        }
        if (this.prevButton) {
            this.prevButton.off(ButtonEvent.CLICK, this.prevPage);
        }
        if (this.closeButton) {
            this.closeButton.off(ButtonEvent.CLICK, this.hide);
        }

        this.pageJumpListeners.forEach((listener, button) => button.off(ButtonEvent.CLICK, listener));
        this.pageJumpListeners.clear();
    }

    protected update() {
        if (this.mode === PaginationMode.PAGIFY) {
            this.contentLayer.setScene(this.contentSceneNames[this.page]).execute();
        } else {
            const target = this.scrollOrigin + this.pageWidth * (this.totalPages - this.page - 1);

            if (this.scrollTween) {
                this.scrollTween.stop();
            }

            this.scrollTween = new TweenJS.Tween(this.contentLayer.container)
                .to({x: target}, Math.abs((target - this.contentLayer.container.x) / this.pageWidth) * 250)
                .start();
        }

        this.updateControls();

        if (this.pageNumber) {
            this.pageNumber.setText((this.page + 1) + "/" + (this.totalPages));
        }
    }

    protected updateControls() {
        this.reset();

        if (this.nextButton) {
            this.nextButton.on(ButtonEvent.CLICK, this.nextPage);

            this.nextButton.setEnabled(this.page < this.totalPages - 1);
        }

        if (this.prevButton) {
            this.prevButton.on(ButtonEvent.CLICK, this.prevPage);

            this.prevButton.setEnabled(this.page > 0);
        }

        if (this.closeButton) {
            this.closeButton.on(ButtonEvent.CLICK, this.hide);
        }

        for (let i = 0; i < this.totalPages; i++) {
            const pageIndex = i;
            const pageName = this.contentSceneNames[pageIndex];
            const button = this.uiLayer.getFlexiButton(pageName);
            if (button) {
                button.setEnabled(true);

                const listener = () => this.jumpToPage(pageIndex);
                button.on(ButtonEvent.CLICK, listener);
                this.pageJumpListeners.set(button, listener);

                if (pageIndex === this.page) {
                    button.setEnabled(false);
                }
            }
        }
    }

    protected jumpToPage(page: number) {
        this.page = page;

        this.update();
    }

    protected nextPage = () => {
        this.page++;
        this.update();
    }

    protected prevPage = () => {
        this.page--;
        this.update();
    }

    private setupScrollableContent() {
        const bounds = this.contentLayer.bounds;
        this.pageWidth = bounds.width;

        this.contentLayer.defaultScene().execute();

        for (const scene of this.contentSceneNames) {
            for (const child of this.contentLayer.container.children) {
                child.x -= this.pageWidth;
            }

            this.contentLayer.jumpToScene(scene, false);
        }

        this.scrollOrigin = this.contentLayer.container.x;
        this.contentLayer.container.x = this.scrollOrigin + this.pageWidth * (this.totalPages - 1);

        const mask = new PIXI.Graphics();
        mask.beginFill(0xff0000, 1);
        mask.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);

        this.contentLayer.container.mask = mask;
    }
}

export enum PaginationMode {
    PAGIFY = "pagify",
    SCROLL = "scroll"
}
